<template>
  <Content
    class="ai-order-entry"
    :header-label="t('aiOrderEntry.aiOrderEntry')"
    :back-btn-label="
      t('common.backToParam', {
        param: t('common.newParam', { param: t('orders.orders') }),
      })
    "
    :back-btn-route="{ name: 'orderEntry' }"
    :show-toolbar="!!selectedClient"
  >
    <template #toolbar>
      <div
        role="toolbar"
        class="edit-bar flex flex-gap-1 justify-content-center"
      >
        <RygenButton
          mode="primary"
          :disabled="!canSubmit"
          :loading="submitting"
          :label="t('aiOrderEntry.createDraftOrders')"
          icon="pi pi-file-import"
          juicy
          @click="onSubmit"
        />
      </div>
    </template>

    <div class="ai-order-entry-form flex flex-column">
      <InputWrapper
        v-slot="{ ariaProps, inputId }"
        :input-id="`${uid}-instructions`"
        :label="t('aiOrderEntry.instructions')"
        optional
      >
        <Textarea
          :id="inputId"
          v-model="instructions"
          v-bind="ariaProps"
          rows="4"
          auto-resize
          :placeholder="t('aiOrderEntry.instructions')"
        />
      </InputWrapper>

      <div class="documents flex flex-column">
        <label>{{ t('aiOrderEntry.documents') }}</label>
        <FileUpload
          :show-cancel-button="false"
          :show-upload-button="false"
          custom-upload
          multiple
          :file-limit="100"
          accept=".txt,.csv,.pdf,.jpg,.jpeg,.png,.xlsx"
          :choose-label="
            t('common.chooseParam', { param: t('common.files', 2) })
          "
          @select="onFilesSelected"
          @remove="onFileRemoved"
        >
          <template #empty>
            <FontAwesomeIcon
              :icon="['fas', 'cloud-upload']"
              class="upload-icon"
            />
            <p class="upload-instructions text-gray-darkest">
              {{ t('aiOrderEntry.dragYourDocumentsHere') }}
            </p>
            <p class="upload-file-types text-gray-dark">
              {{ t('aiOrderEntry.supportedFileTypes') }}
            </p>
          </template>
        </FileUpload>
      </div>
    </div>

    <ChangeDomainModal
      v-model:visible="isChangeDomainModalVisible"
      :selected-domain="selectedClient"
      :selectable-domain-types="[DomainType.Client]"
      :domain-options="clients.data"
      :header="t('common.selectParam', { param: t('clients.clients') })"
      position="center"
      closable
      @change-domain="onClientChange"
    />
  </Content>
</template>

<script lang="ts" setup>
import { useFlag } from '@unleash/proxy-client-vue';
import axios from 'axios';
import { computed, onMounted, Ref, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import {
  useToast,
  useUid,
  useUnsavedChangesConfirmation,
} from '@rygen/rygen-ui/composables';
import { DomainReference, DomainTree, DomainType } from '@rygen/rygen-ui/model';

import apiInstance from '@/api';
import { DocumentProperties } from '@/model';
import { useClientStore } from '@/stores/Client.ts';
import { useCurrentUserStore } from '@/stores/CurrentUser.ts';

import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import Content from '@rygen/rygen-ui/components/content/Content.vue';
import ChangeDomainModal from '@rygen/rygen-ui/components/domain/ChangeDomainModal.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import {
  default as FileUpload,
  FileUploadRemoveEvent,
  FileUploadSelectEvent,
} from 'primevue/fileupload';
import Textarea from 'primevue/textarea';

const { uid } = useUid('ai-order-entry');
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const toast = useToast();
const { clientTreeMap, clients, loadClients } = useClientStore();
const { selectedDomain } = useCurrentUserStore();

const isAiOrderEntryEnabled = useFlag(
  'corsair-ai-service-order-entry-feat-flag',
);

const isChangeDomainModalVisible = ref(false);
const selectedClient: Ref<DomainTree | undefined> = ref();
const files: Ref<File[]> = ref([]);
const instructions = ref('');
const submitting = ref(false);

const canSubmit = computed(
  () => !!selectedClient.value && files.value.length > 0 && !submitting.value,
);

const onFilesSelected = (event: FileUploadSelectEvent) => {
  files.value = Array.isArray(event.files) ? [...event.files] : [];
};

const onFileRemoved = (event: FileUploadRemoveEvent) => {
  files.value = Array.isArray(event.files) ? [...event.files] : [];
};

const onClientChange = (newClient: DomainReference) => {
  selectedClient.value = newClient.id
    ? clientTreeMap.value.get(newClient.id)
    : undefined;
};

const uploadDocument = async (file: File): Promise<DocumentProperties> => {
  const uploadUrl = await apiInstance.aiOrderEntry.getUploadUrl(
    file.name,
    file.type,
  );

  await axios.put(uploadUrl.uploadUrl, file, {
    headers: { 'Content-Type': file.type },
  });

  return DocumentProperties.fromFile(file, { fileName: uploadUrl.fileName });
};

const resetState = () => {
  files.value = [];
  instructions.value = '';
};

const onSubmit = async () => {
  if (!selectedClient.value?.id || files.value.length === 0) {
    return;
  }

  try {
    submitting.value = true;

    const documents = await Promise.all(files.value.map(uploadDocument));

    await apiInstance.aiOrderEntry.submit(
      selectedClient.value.id,
      instructions.value.trim(),
      documents,
    );

    resetState();

    toast.addSuccess({
      detail: t('aiOrderEntry.requestSubmitted'),
    });

    // TODO CS-11788: send the operator to the Draft Orders page instead
    router.push({ name: 'aiOrderEntryDraftList' });
  } catch (e: any) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('failed to submit AI order entry request', e);
    toast.addError({
      error: e,
      detail: t('errors.creatingParam', {
        param: t('aiOrderEntry.draftOrders'),
      }),
    });
  } finally {
    submitting.value = false;
  }
};

const updateSelectedClient = (client: DomainReference | undefined) => {
  selectedClient.value =
    client?.domainType === DomainType.Client && client.id
      ? clientTreeMap.value.get(client.id)
      : undefined;

  isChangeDomainModalVisible.value = !selectedClient.value;
};

useUnsavedChangesConfirmation({
  isDirtyCallback: async () => {
    const isDirty = files.value.length > 0 || !!instructions.value;
    return isDirty && !submitting.value;
  },
});

watch(selectedDomain, async (newVal: DomainReference | undefined) => {
  await loadClients();
  updateSelectedClient(newVal);
});

watch(isChangeDomainModalVisible, (newVal: boolean, oldVal: boolean) => {
  if (oldVal && !newVal && !selectedClient.value) {
    router.back();
  }
});

onMounted(async () => {
  if (!isAiOrderEntryEnabled.value) {
    router.replace({ name: 'orderEntry' });
    return;
  }

  await loadClients();

  const clientIdParam = Number(route.query.clientId);
  const client = Number.isFinite(clientIdParam)
    ? clientTreeMap.value.get(clientIdParam)
    : undefined;

  if (client) {
    selectedClient.value = client;
    return;
  }

  updateSelectedClient(selectedDomain.value);
});
</script>

<style>
.ai-order-entry {
  .edit-bar {
    padding: var(--sp-3);
  }

  .ai-order-entry-form {
    gap: var(--sp-4);

    .documents {
      gap: var(--sp-2);
    }

    .upload-icon {
      font-size: 32px;
    }

    .upload-instructions {
      margin: var(--sp-2) 0 0;
    }

    .upload-file-types {
      margin: 0;
    }
  }
}
</style>
